(() => {
  const init = () => {
    const drawer = document.querySelector('#mobile-navigation');
    const trigger = document.querySelector('[data-action="toggle-nav"]');
    const closeButton = drawer?.querySelector(':scope > close-menu button');
    const desktopNavigation = document.querySelector('#desktop-navigation');
    const header = document.querySelector('.b2h-native-header');
    const accountTrigger = document.querySelector('[data-account-dropdown-trigger]');
    const accountMenu = document.querySelector('[data-account-dropdown-menu]');
    const accountWrapper = accountTrigger?.closest('[data-account-dropdown-wrapper]');
    const cartAction = document.querySelector('#cart-icon-bubble');
    const cartCount = cartAction?.querySelector('.b2h-header-action__count');

    if (!drawer || !trigger) return false;
    if (drawer.dataset.b2hNativeHeaderInitialized === 'true') return true;
    drawer.dataset.b2hNativeHeaderInitialized = 'true';

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])',
    ].join(',');

    const isOpen = () => document.documentElement.classList.contains('nav-open');
    let activeSubmenuTrigger = null;
    let topLevelScrollTop = 0;
    let cancelDrawerClose = null;

    const desktopLevels = desktopNavigation
      ? [...desktopNavigation.querySelectorAll(':scope .horizontal-list > .level0')]
      : [];
    let activeDesktopLevel = null;
    let desktopEnterTimer = null;
    let desktopExitTimer = null;
    let desktopSwitchFrame = null;
    let desktopSwitchCleanupFrame = null;
    let restoringDesktopFocus = false;

    const setAccountOpen = (open, { restoreFocus = false } = {}) => {
      if (!accountTrigger || !accountMenu) return;

      accountTrigger.setAttribute('aria-expanded', String(open));
      if (open) {
        accountMenu.hidden = false;
        window.requestAnimationFrame(() => accountMenu.classList.add('is-open'));
      } else {
        accountMenu.classList.remove('is-open');
        accountMenu.hidden = true;
        if (restoreFocus) accountTrigger.focus({ preventScroll: true });
      }
    };

    if (accountWrapper && accountTrigger && accountMenu) {
      accountTrigger.addEventListener('click', (event) => {
        event.stopPropagation();
        setAccountOpen(accountTrigger.getAttribute('aria-expanded') !== 'true');
      });

      document.addEventListener('click', (event) => {
        if (!accountWrapper.contains(event.target)) setAccountOpen(false);
      });
    }

    const syncCartCount = () => {
      if (!cartCount) return;
      const visibleCount = cartCount.textContent.trim();
      const count = Number.parseInt(visibleCount, 10);
      const hasItems = visibleCount.startsWith('~') || (Number.isFinite(count) && count > 0);
      cartCount.dataset.empty = String(!hasItems);
      if (cartAction) {
        cartAction.setAttribute('aria-label', `${cartAction.dataset.cartLabel}, ${hasItems ? visibleCount : 0}`);
      }
    };

    if (cartCount) {
      syncCartCount();
      new MutationObserver(syncCartCount).observe(cartCount, { childList: true, subtree: true, characterData: true });
    }

    const setDesktopExpanded = (level, expanded) => {
      level
        ?.querySelector(':scope > menu-item > open-children-toggle')
        ?.setAttribute('aria-expanded', String(expanded));
    };

    const endDesktopMenuSwitch = () => {
      window.cancelAnimationFrame(desktopSwitchFrame);
      window.cancelAnimationFrame(desktopSwitchCleanupFrame);
      desktopSwitchFrame = null;
      desktopSwitchCleanupFrame = null;
      header?.classList.remove('is-switching-menu');
    };

    const beginDesktopMenuSwitch = () => {
      endDesktopMenuSwitch();
      header?.classList.add('is-switching-menu');

      // Keep the no-transition state through one paint. Removing it in the
      // following frame preserves first-open/last-close motion while making
      // an already-open mega-menu panel swap atomic.
      desktopSwitchFrame = window.requestAnimationFrame(() => {
        desktopSwitchFrame = null;
        desktopSwitchCleanupFrame = window.requestAnimationFrame(() => {
          desktopSwitchCleanupFrame = null;
          header?.classList.remove('is-switching-menu');
        });
      });
    };

    const closeDesktopMenu = ({ restoreFocus = false } = {}) => {
      window.clearTimeout(desktopEnterTimer);
      window.clearTimeout(desktopExitTimer);
      endDesktopMenuSwitch();
      if (!activeDesktopLevel) return;

      const level = activeDesktopLevel;
      activeDesktopLevel = null;
      level.classList.remove('visible', 'active-submenu');
      level.isActive = false;
      setDesktopExpanded(level, false);
      header?.classList.remove('active', 'open-submenu');

      if (restoreFocus) {
        // Focus restoration fires focusin synchronously. Do not let that
        // deliberate move reopen the menu that Escape just closed.
        restoringDesktopFocus = true;
        try {
          level.querySelector(':scope > menu-item > a')?.focus({ preventScroll: true });
        } finally {
          restoringDesktopFocus = false;
        }
      }
    };

    const openDesktopMenu = (level) => {
      if (!level?.classList.contains('menu-parent')) {
        closeDesktopMenu();
        return;
      }

      window.clearTimeout(desktopExitTimer);
      if (activeDesktopLevel && activeDesktopLevel !== level) {
        const previousLevel = activeDesktopLevel;
        beginDesktopMenuSwitch();
        previousLevel.classList.remove('visible', 'active-submenu');
        previousLevel.isActive = false;
        setDesktopExpanded(previousLevel, false);
      }

      activeDesktopLevel = level;
      level.classList.add('visible', 'active-submenu');
      level.isActive = true;
      setDesktopExpanded(level, true);
      header?.classList.add('active', 'open-submenu');
    };

    desktopLevels.forEach((level) => {
      level.addEventListener('pointerenter', () => {
        window.clearTimeout(desktopExitTimer);
        window.clearTimeout(desktopEnterTimer);
        if (level === activeDesktopLevel) return;
        desktopEnterTimer = window.setTimeout(() => openDesktopMenu(level), 110);
      });

      level.addEventListener('pointerleave', () => {
        window.clearTimeout(desktopEnterTimer);
        if (level !== activeDesktopLevel) return;
        desktopExitTimer = window.setTimeout(() => closeDesktopMenu(), 180);
      });

      level.addEventListener('focusin', () => {
        if (restoringDesktopFocus) return;
        window.clearTimeout(desktopEnterTimer);
        openDesktopMenu(level);
      });

      level.addEventListener('focusout', (event) => {
        if (event.relatedTarget && level.contains(event.relatedTarget)) return;
        desktopExitTimer = window.setTimeout(() => closeDesktopMenu(), 0);
      });
    });

    desktopNavigation?.addEventListener('click', (event) => {
      const toggle = event.target.closest?.('.level0 > menu-item > open-children-toggle');
      if (!toggle) return;
      const level = toggle.closest('.level0');
      if (level === activeDesktopLevel) closeDesktopMenu();
      else openDesktopMenu(level);
    });

    const getActiveMobileItem = () => drawer.querySelector(
      '#mobile-menu-horizontal-panel > .horizontal-list > .level0 > menu-item.is-open'
    );

    const resetMobileInlineScroll = () => {
      drawer.scrollLeft = 0;
      const menuContent = drawer.querySelector('.navigation__menu-content-mobile');
      if (menuContent) menuContent.scrollLeft = 0;
    };

    const focusAfterLayerChange = (element) => {
      if (!element) return;
      const focus = () => {
        element.focus({ preventScroll: true });
        resetMobileInlineScroll();
        window.requestAnimationFrame(resetMobileInlineScroll);
      };
      window.requestAnimationFrame(() => {
        if (getComputedStyle(element).visibility !== 'hidden') focus();
        else window.setTimeout(focus, 180);
      });
    };

    const syncMenuLayer = ({ moveFocus = false, restoreFocus = false } = {}) => {
      const topLevels = drawer.querySelectorAll('#mobile-menu-horizontal-panel > .horizontal-list > .level0');
      const activeItem = getActiveMobileItem();
      const activeLevel = activeItem?.closest('.level0');

      topLevels.forEach((level) => {
        const item = level.querySelector(':scope > menu-item');
        const submenu = level.querySelector(':scope > .submenu');
        const toggle = item?.querySelector(':scope > open-children-toggle');
        const isActive = level === activeLevel;
        toggle?.setAttribute('aria-expanded', String(isActive));
        level.toggleAttribute('inert', Boolean(activeLevel) && !isActive);
        item?.toggleAttribute('inert', Boolean(activeLevel));
        submenu?.toggleAttribute('inert', !isActive);
      });

      drawer.querySelector('.b2h-mobile-menu-assistance')?.toggleAttribute('inert', Boolean(activeLevel));
      const menuContent = drawer.querySelector('.navigation__menu-content-mobile');
      resetMobileInlineScroll();
      if (menuContent) {
        if (activeItem && moveFocus) {
          topLevelScrollTop = menuContent.scrollTop;
          menuContent.scrollTop = 0;
        } else if (!activeItem && restoreFocus) {
          menuContent.scrollTop = topLevelScrollTop;
        }
      }

      if (activeItem && moveFocus) {
        const nextFocus = activeLevel?.querySelector(':scope > .submenu back-menu');
        focusAfterLayerChange(nextFocus);
      } else if (!activeItem && restoreFocus && activeSubmenuTrigger) {
        const nextFocus = activeSubmenuTrigger;
        activeSubmenuTrigger = null;
        focusAfterLayerChange(nextFocus);
      }
    };

    const resetMobileLevels = () => {
      drawer.querySelectorAll('#mobile-menu-horizontal-panel > .horizontal-list > .level0 > menu-item.is-open')
        .forEach((item) => item.classList.remove('is-open'));
      activeSubmenuTrigger = null;
      syncMenuLayer();
    };

    const setDrawerOpen = (open, { moveFocus = false, restoreFocus = false } = {}) => {
      cancelDrawerClose?.();
      cancelDrawerClose = null;

      trigger.setAttribute('aria-expanded', String(open));
      trigger.classList.toggle('open', open);

      if (open) {
        window.B2HDrawer?.setState(drawer, 'opening');
        drawer.setAttribute('aria-hidden', 'false');
        drawer.removeAttribute('inert');
        const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
        if (scrollbarWidth > 0) document.documentElement.style.setProperty('padding-right', `${scrollbarWidth}px`);
        document.documentElement.classList.add('nav-open', 'open-drawer');
        window.requestAnimationFrame(() => {
          if (!isOpen()) return;
          cancelDrawerClose = window.B2HDrawer?.afterMotion(drawer, () => {
            if (isOpen()) window.B2HDrawer?.setState(drawer, 'open');
            cancelDrawerClose = null;
          }) || null;
          if (!cancelDrawerClose) window.B2HDrawer?.setState(drawer, 'open');
        });
        syncMenuLayer();
        if (moveFocus) window.requestAnimationFrame(() => closeButton?.focus({ preventScroll: true }));
        return;
      }

      window.B2HDrawer?.setState(drawer, 'closing');
      document.documentElement.classList.remove('nav-open');
      resetMobileLevels();
      const finishClose = () => {
        if (!isOpen()) {
          window.B2HDrawer?.setState(drawer, 'closed');
          drawer.setAttribute('aria-hidden', 'true');
          drawer.setAttribute('inert', '');
          document.documentElement.classList.remove('open-drawer');
          document.documentElement.style.removeProperty('padding-right');
          if (restoreFocus) trigger.focus({ preventScroll: true });
        }
      };
      cancelDrawerClose = window.B2HDrawer?.afterMotion(drawer, finishClose) || null;
      if (!cancelDrawerClose) finishClose();
    };

    const closeHeaderDisclosuresForPopup = () => {
      closeDesktopMenu();
      desktopLevels.forEach((level) => {
        level.classList.remove('visible', 'active-submenu');
        level.isActive = false;
        setDesktopExpanded(level, false);
      });
      header?.classList.remove('active', 'open-submenu', 'is-switching-menu');
      setAccountOpen(false);
      if (isOpen()) setDrawerOpen(false);
    };

    document.addEventListener('b2h:popup-open', closeHeaderDisclosuresForPopup);

    const openMobileLevel = (toggle) => {
      const item = toggle?.closest('menu-item');
      if (!item) return;

      drawer.querySelectorAll('#mobile-menu-horizontal-panel > .horizontal-list > .level0 > menu-item.is-open')
        .forEach((openItem) => {
          if (openItem !== item) openItem.classList.remove('is-open');
        });
      const shouldOpen = !item.classList.contains('is-open');
      item.classList.toggle('is-open', shouldOpen);
      activeSubmenuTrigger = shouldOpen ? toggle : null;
      syncMenuLayer({ moveFocus: shouldOpen, restoreFocus: !shouldOpen });
    };

    const closeMobileLevel = ({ restoreFocus = true } = {}) => {
      const activeItem = getActiveMobileItem();
      if (!activeItem) return false;
      activeItem.classList.remove('is-open');
      syncMenuLayer({ restoreFocus });
      return true;
    };

    if (!window.isB2HNativeHeaderToggleDelegated) {
      window.isB2HNativeHeaderToggleDelegated = true;
      document.addEventListener('click', (event) => {
        const navTrigger = event.target.closest?.('[data-action="toggle-nav"]');
        if (navTrigger) {
          event.preventDefault();
          event.stopPropagation();
          setDrawerOpen(!isOpen(), { moveFocus: !isOpen(), restoreFocus: isOpen() });
          return;
        }

        if (event.target.closest?.('#mobile-navigation close-menu button')) {
          event.preventDefault();
          event.stopPropagation();
          setDrawerOpen(false, { restoreFocus: true });
          return;
        }

        const submenuTrigger = event.target.closest?.(
          '#mobile-navigation .level0 > menu-item > open-children-toggle'
        );
        if (submenuTrigger) {
          event.preventDefault();
          event.stopPropagation();
          openMobileLevel(submenuTrigger);
          return;
        }

        const backButton = event.target.closest?.('#mobile-navigation .level0 > .submenu back-menu');
        if (backButton) {
          event.preventDefault();
          event.stopPropagation();
          closeMobileLevel({ restoreFocus: true });
          return;
        }

        if (isOpen() && !drawer.contains(event.target)) {
          setDrawerOpen(false, { restoreFocus: true });
        }
      }, true);
    }

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const submenuTrigger = event.target.closest?.(
        '#mobile-navigation .level0 > menu-item > open-children-toggle'
      );
      const backButton = event.target.closest?.('#mobile-navigation .level0 > .submenu back-menu');
      if (!submenuTrigger && !backButton) return;

      event.preventDefault();
      event.stopPropagation();
      if (submenuTrigger) openMobileLevel(submenuTrigger);
      else closeMobileLevel({ restoreFocus: true });
    }, true);

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && activeDesktopLevel) {
        event.preventDefault();
        closeDesktopMenu({ restoreFocus: true });
        return;
      }

      if (!isOpen()) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        if (!closeMobileLevel({ restoreFocus: true })) {
          setDrawerOpen(false, { restoreFocus: true });
        }
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = [...drawer.querySelectorAll(focusableSelector)]
        .filter((element) => !element.closest('[inert]') && element.getClientRects().length > 0);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    window.addEventListener('resize', () => {
      if (window.innerWidth >= 1280 && isOpen()) {
        setDrawerOpen(false);
      }
    });

    accountTrigger?.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown') return;
      event.preventDefault();
      setAccountOpen(true);
      window.requestAnimationFrame(() => accountMenu?.querySelector(focusableSelector)?.focus());
    });

    accountWrapper?.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || accountTrigger?.getAttribute('aria-expanded') !== 'true') return;
      event.preventDefault();
      setAccountOpen(false, { restoreFocus: true });
    });

    trigger.setAttribute('aria-expanded', String(isOpen()));
    drawer.setAttribute('aria-hidden', String(!isOpen()));
    drawer.toggleAttribute('inert', !isOpen());
    if (isOpen()) syncMenuLayer();
    else resetMobileLevels();
    return true;
  };

  const boot = (attempt = 0) => {
    if (init() || attempt >= 20) return;
    window.setTimeout(() => boot(attempt + 1), 50);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => boot(), { once: true });
  } else {
    boot();
  }
})();

/* The original native controls own all functions. Only V6 light/dark presentation follows scroll. */
(()=>{const hero=document.getElementById('hero');const paint=()=>{const r=hero?.getBoundingClientRect();document.documentElement.dataset.b2hHeader=r&&r.bottom>115&&r.top<90?'dark':'light';};let frame=0;const schedule=()=>{if(!frame)frame=requestAnimationFrame(()=>{frame=0;paint();});};addEventListener('scroll',schedule,{passive:true});addEventListener('resize',schedule);paint();})();
